import { useEffect, useRef, useState } from "react";
import { Triangle, Diamond, Circle, Square } from "lucide-react";
import { api, API, mediaUrl } from "./api";

// Option visual metadata — color + shape (colorblind-safe)
export const OPTIONS = [
  { key: "a", shape: Triangle, bg: "bg-rose-600", border: "border-rose-300", hex: "#f43f5e", active: "active:bg-rose-700", glow: "shadow-[0_0_28px_rgba(244,63,94,0.55)]" },
  { key: "b", shape: Diamond, bg: "bg-cyan-600", border: "border-cyan-200", hex: "#06b6d4", active: "active:bg-cyan-700", glow: "shadow-[0_0_28px_rgba(6,182,212,0.55)]" },
  { key: "c", shape: Circle, bg: "bg-amber-500", border: "border-amber-100", hex: "#f59e0b", active: "active:bg-amber-600", glow: "shadow-[0_0_28px_rgba(245,158,11,0.55)]" },
  { key: "d", shape: Square, bg: "bg-emerald-500", border: "border-emerald-100", hex: "#10b981", active: "active:bg-emerald-600", glow: "shadow-[0_0_28px_rgba(16,185,129,0.55)]" },
];

export function ShapeIcon({ index, className = "w-6 h-6" }) {
  const Shape = OPTIONS[index]?.shape || Circle;
  return <Shape className={className} strokeWidth={3} fill="currentColor" />;
}

export function Avatar({ player, size = 56, ring = true }) {
  const url = mediaUrl(player?.avatar_url);
  const initial = (player?.name || "?").charAt(0).toUpperCase();
  const style = {
    width: size, height: size,
    boxShadow: ring ? `0 0 0 3px ${player?.color || "#FF2A85"}, 0 0 18px ${(player?.color || "#FF2A85")}66` : "none",
    background: player?.color || "#1b1130",
  };
  return (
    <div className="rounded-full overflow-hidden flex items-center justify-center shrink-0" style={style}>
      {url ? (
        <img src={url} alt={player?.name} className="w-full h-full object-cover" />
      ) : (
        <span className="font-display font-bold text-white" style={{ fontSize: size * 0.4 }}>{initial}</span>
      )}
    </div>
  );
}

// Smooth local countdown, resynced whenever server time_left changes
export function useCountdown(serverTimeLeft, limit) {
  const [t, setT] = useState(serverTimeLeft ?? limit);
  const ref = useRef(serverTimeLeft ?? limit);
  useEffect(() => {
    if (serverTimeLeft == null) return;
    ref.current = serverTimeLeft;
    setT(serverTimeLeft);
    const id = setInterval(() => {
      ref.current = Math.max(0, ref.current - 0.1);
      setT(ref.current);
    }, 100);
    return () => clearInterval(id);
  }, [serverTimeLeft]);
  return Math.max(0, t);
}

// Live room state: server push over WebSocket, falling back to HTTP polling while disconnected
export function useRoomState(code, viewer, intervalMs = 1500) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!code) return;
    let alive = true, ws = null, pollId = null, retryId = null, watchId = null, lastMsg = 0, fails = 0;
    const q = viewer ? `?viewer=${encodeURIComponent(viewer)}` : "";
    const poll = async () => {
      try {
        const res = await api.get(`/rooms/${code}/state${q}`);
        if (alive) { setState(res.data); setError(null); }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Connection lost");
      }
    };
    const startPolling = () => { if (!pollId) { poll(); pollId = setInterval(poll, intervalMs); } };
    const stopPolling = () => { if (pollId) { clearInterval(pollId); pollId = null; } };
    const connect = () => {
      if (!alive || typeof WebSocket === "undefined") return startPolling();
      try { ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/rooms/${code}${q}`); } catch (e) { return startPolling(); }
      ws.onmessage = (ev) => {
        lastMsg = Date.now(); fails = 0;
        try { const d = JSON.parse(ev.data); if (alive) { stopPolling(); setState(d); setError(null); } } catch (e) { /* ignore */ }
      };
      ws.onclose = (ev) => {
        ws = null;
        if (!alive) return;
        if (ev.code === 4404) { stopPolling(); setError("Game not found."); return; }
        fails += 1;
        startPolling();
        retryId = setTimeout(connect, Math.min(15000, 1000 * Math.pow(2, Math.min(fails, 4))));
      };
      ws.onerror = () => { try { ws && ws.close(); } catch (e) { /* ignore */ } };
    };
    connect();
    // If the socket goes silent (server pushes at least every few seconds), force a reconnect.
    watchId = setInterval(() => {
      if (ws && ws.readyState === 1 && lastMsg && Date.now() - lastMsg > 9000) { try { ws.close(); } catch (e) { /* ignore */ } }
    }, 3000);
    return () => {
      alive = false; stopPolling(); clearTimeout(retryId); clearInterval(watchId);
      if (ws) { ws.onclose = null; try { ws.close(); } catch (e) { /* ignore */ } }
    };
  }, [code, viewer, intervalMs]);
  return { state, error };
}

export function TimerRing({ timeLeft, limit, size = 150 }) {
  const frac = limit ? Math.max(0, timeLeft / limit) : 0;
  const r = size / 2 - 8;
  const circ = 2 * Math.PI * r;
  const danger = timeLeft <= 5;
  const color = danger ? "#f43f5e" : timeLeft <= limit * 0.4 ? "#FFD700" : "#00F0FF";
  return (
    <div className={`relative ${danger ? "animate-danger rounded-full" : ""}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.1)" strokeWidth="10" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="10" fill="none"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - frac)}
          style={{ transition: "stroke-dashoffset 0.1s linear, stroke 0.3s" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display font-black" style={{ fontSize: size * 0.3, color }}>{Math.ceil(timeLeft)}</span>
      </div>
    </div>
  );
}
